// Privacy-aware GA4 + GTM loader. Mounts only on public marketing routes.
// Honors Consent Mode v2 — sends 'denied' defaults before any tag fires.

// Single source of truth for what counts as private / internal traffic.
// Used by:
//  - GA4 + GTM loaders (don't fire on these routes)
//  - first-party visitor tracker (don't ingest events on these routes)
//  - the ga4-report edge function (server-side pagePath filter)
//  - the visitor_events_public DB view (defense in depth)
export const EXCLUDED_PATH_PREFIXES = [
  "/cws-admin",
  "/admin",
  "/dashboard",
  "/customer",
  "/onboarding",
  "/auth",
  "/auth-callback",
  "/login",
  "/signup",
  "/reset-password",
  "/forgot-password",
  "/resend-verification",
  "/unsubscribe",
] as const;

export const isExcludedPath = (path: string): boolean => {
  if (!path) return false;
  return EXCLUDED_PATH_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
};

export const isPublicMarketingPath = (path: string): boolean => {
  if (!path) return false;
  return !isExcludedPath(path);
};

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    __cwsGaLoaded?: boolean;
    __cwsGtmLoaded?: boolean;
    __cwsConsentDefaultsSet?: boolean;
  }
}

// Hardcoded fallbacks (publishable IDs — safe in client code).
// These mirror what's already loaded from index.html so the admin badges
// and the loader agree on what's actually live in production.
export const HARDCODED_GTM_ID = "GTM-T8FKZ898";
export const HARDCODED_GA_ID = "G-EYGX8GD1ZT";

const ENV_GA_ID = (import.meta as any).env?.VITE_GA_MEASUREMENT_ID as string | undefined;
const ENV_GTM_ID = (import.meta as any).env?.VITE_GTM_CONTAINER_ID as string | undefined;

export const GA_ID: string | undefined =
  ENV_GA_ID && /^G-[A-Z0-9]+$/i.test(ENV_GA_ID) ? ENV_GA_ID : (HARDCODED_GA_ID || undefined);
export const GTM_ID: string | undefined =
  ENV_GTM_ID && /^GTM-[A-Z0-9]+$/i.test(ENV_GTM_ID) ? ENV_GTM_ID : (HARDCODED_GTM_ID || undefined);

export const isGaConfigured = () => !!GA_ID && /^G-[A-Z0-9]+$/i.test(GA_ID);
export const isGtmConfigured = () => !!GTM_ID && /^GTM-[A-Z0-9]+$/i.test(GTM_ID);

function ensureGtagShim() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
}

export function setConsentDefaults() {
  if (typeof window === "undefined" || window.__cwsConsentDefaultsSet) return;
  ensureGtagShim();
  window.gtag!("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500,
  });
  window.__cwsConsentDefaultsSet = true;
}

export function updateConsentMode(prefs: { analytics: boolean; functional: boolean; advertising: boolean }) {
  if (typeof window === "undefined") return;
  ensureGtagShim();
  window.gtag!("consent", "update", {
    analytics_storage: prefs.analytics ? "granted" : "denied",
    ad_storage: prefs.advertising ? "granted" : "denied",
    ad_user_data: prefs.advertising ? "granted" : "denied",
    ad_personalization: prefs.advertising ? "granted" : "denied",
    functionality_storage: prefs.functional ? "granted" : "denied",
    personalization_storage: prefs.functional ? "granted" : "denied",
  });
}

export function loadGa4Once() {
  if (typeof window === "undefined") return;
  if (window.__cwsGaLoaded || !isGaConfigured()) return;
  setConsentDefaults();
  window.__cwsGaLoaded = true;
  // gtag.js is already loaded inline from index.html (so Google's site
  // scanner can verify installation). Just ensure the shim exists; SPA
  // page_views are fired via gaPageView() on every route change.
  ensureGtagShim();
}

export function loadGtmOnce() {
  if (typeof window === "undefined") return;
  if (window.__cwsGtmLoaded || !isGtmConfigured()) return;
  setConsentDefaults();
  window.__cwsGtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
}

export function gaPageView(path: string) {
  if (!window.gtag || !isGaConfigured()) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function gtmVirtualPageView(path: string) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "page_view",
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function gaEvent(name: string, params?: Record<string, any>) {
  if (!window.gtag || !isGaConfigured()) return;
  window.gtag("event", name, params || {});
}
